import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { NgxsFormPluginModule } from '@ngxs/form-plugin';
import { TranslateModule } from '@ngx-translate/core';
import { Select, ofActionSuccessful } from '@ngxs/store';
import { Observable, filter, map, of, takeUntil, withLatestFrom } from 'rxjs';

import { AngularMaterialModule } from '@app/shared/modules/angular-material.module';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { EditModalDialogBasedComponent } from '@shared/components/edit-modal-dialog/edit-modal-dialog.based.component';
import { Driver } from '@app/shared/classes/driver';
import { NgxsActionName, NgxsFormPath } from '@app/shared/constants';
import { AppDataState } from '@app/shared/stores/app-data.state';
import { DropDownItem, WebApiResponseBase } from '@app/shared/classes/general';
import { InjectDialogData } from '@shared/components/edit-modal-dialog/edit-modal-dialog.constant';
import * as AppDataAction from '@app/shared/actions/app-data.action';
import { SpinnerButtonComponent } from '@shared/components/spinner-button/spinner-button.component';
import { Terminal } from '@app/shared/classes/terminal';
import { CommonDataState } from '@app/shared/stores/common-data.state';
import { UserProfile } from '@app/shared/classes/user-profile';

@Component({
  selector: 'app-driver-data-edit-modal-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslateModule,
    AngularMaterialModule,
    NgxsFormPluginModule,
    ReactiveFormsModule,
    MatDialogModule,
    SpinnerButtonComponent,
    CommonModule],
  templateUrl: './driver-data-edit-modal-dialog.component.html',
  styleUrl: './driver-data-edit-modal-dialog.component.css'
})
export class DriverDataEditModalDialogComponent extends EditModalDialogBasedComponent<DriverDataEditModalDialogComponent, Driver> {
  formPath = NgxsFormPath.DRIVER_DATA_EDIT_FORM;
  selectedBusinessType$: Observable<string> = of("");

  terminalSelectItems$: Observable<DropDownItem[]>;

  @Select(CommonDataState.profileMe) profileMe$: Observable<UserProfile>;
  @Select(AppDataState.taskResponse(NgxsActionName.GET_DRIVER_RELATED_TERMINAL_LIST)) terminals$: Observable<Terminal[]>;

  @Select(AppDataState.taskExecuting(NgxsActionName.UPDATE_DRIVER_DATA)) processing$: Observable<boolean>;
  @Select(AppDataState.taskError(NgxsActionName.UPDATE_DRIVER_DATA)) response$: Observable<WebApiResponseBase>;

  constructor(public dialogRef: MatDialogRef<DriverDataEditModalDialogComponent>, @Inject(MAT_DIALOG_DATA) public data: InjectDialogData<Driver>) {
    super();
    this.dataInit(dialogRef, data);
    this.initForm(data);
  }

  initForm = (data: InjectDialogData<Driver>) => {
    this.editForm = this.formBuilder.group({
      driver_Id: [data.data.driver_Id],
      terminal_Id: [data.data.terminal_Id, Validators.required],
    });

    this.selectedBusinessType$ = of(data.data.businessType);
  }

  override ngOnInit() {
    super.ngOnInit();

    this.terminalSelectItems$ = this.terminals$.pipe(filter(Boolean), withLatestFrom(this.profileMe$),
      map(([terminals, profileMe]) => terminals.filter(terminal => terminal.country === profileMe.country)
        .map(terminal => {
          return {
            value: terminal.terminal_Id,
            label: `${terminal.terminalId} ${terminal.terminalName}`
          } as DropDownItem;
        })
      ));

    this.actions$.pipe(ofActionSuccessful(AppDataAction.UpdateDriverData), withLatestFrom(this.response$), takeUntil(this.destroy$)).subscribe(
      ([_, errorResponse]) => this.processAfterUpdateObject(errorResponse));
  }

  onOkClick = () => {
    this.store.dispatch(new AppDataAction.UpdateDriverData());
  }

  onTerminalSelectionChanged = (event: any) => {
    this.selectedBusinessType$ = this.terminals$.pipe(filter(Boolean), map(terminals => {
      var selectedTerminal = terminals.find(item => item.terminal_Id === event.value);
      return selectedTerminal ? selectedTerminal.businessType : "";
    }));
  }
}
